/**
 * Poker Session Stats - Track statistics across multiple games
 */

const STORAGE_KEY = 'boosty_poker_stats';

// Default stats structure
const DEFAULT_STATS = {
  handsPlayed: 0,
  handsWon: 0,
  totalChipsWon: 0,
  totalChipsLost: 0,
  biggestPot: 0,
  bestHand: null,
  bestHandName: null,
  currentStreak: 0,
  longestWinStreak: 0,
  longestLoseStreak: 0,
  handTypeCounts: {},
  sessionStart: null,
  lastPlayed: null
};

// Load stats from localStorage
export const loadStats = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_STATS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Failed to load stats:', e);
  }
  return { ...DEFAULT_STATS };
};

// Save stats to localStorage
export const saveStats = (stats) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  } catch (e) {
    console.error('Failed to save stats:', e);
  }
};

// Update stats after a hand
export const updateStats = (currentStats, handResult) => {
  const {
    won,
    potSize,
    chipsWon,
    chipsLost,
    handName,
    holeCards
  } = handResult;
  
  const newStats = { ...currentStats };
  
  // Basic counters
  newStats.handsPlayed++;
  newStats.lastPlayed = new Date().toISOString();
  
  if (!newStats.sessionStart) {
    newStats.sessionStart = new Date().toISOString();
  }
  
  // Win/loss tracking
  if (won) {
    newStats.handsWon++;
    newStats.totalChipsWon += chipsWon || 0;
    
    // Streak tracking
    if (newStats.currentStreak >= 0) {
      newStats.currentStreak++;
      newStats.longestWinStreak = Math.max(newStats.longestWinStreak, newStats.currentStreak);
    } else {
      newStats.currentStreak = 1;
    }
  } else {
    newStats.totalChipsLost += chipsLost || 0;
    
    // Streak tracking
    if (newStats.currentStreak <= 0) {
      newStats.currentStreak--;
      newStats.longestLoseStreak = Math.max(newStats.longestLoseStreak, Math.abs(newStats.currentStreak));
    } else {
      newStats.currentStreak = -1;
    }
  }
  
  // Biggest pot
  if (potSize > newStats.biggestPot) {
    newStats.biggestPot = potSize;
  }
  
  // Best hand tracking
  const handRankings = [
    'High Card', 'One Pair', 'Two Pair', 'Three of a Kind',
    'Straight', 'Flush', 'Full House', 'Four of a Kind',
    'Straight Flush', 'Royal Flush'
  ];
  
  if (handName) {
    const currentRank = handRankings.indexOf(handName);
    const bestRank = newStats.bestHandName ? handRankings.indexOf(newStats.bestHandName) : -1;
    
    if (currentRank > bestRank) {
      newStats.bestHand = holeCards;
      newStats.bestHandName = handName;
    }
    
    // Track hand type counts
    newStats.handTypeCounts[handName] = (newStats.handTypeCounts[handName] || 0) + 1;
  }
  
  // Save to localStorage
  saveStats(newStats);
  
  return newStats;
};

// Calculate derived stats
export const calculateDerivedStats = (stats) => {
  const winRate = stats.handsPlayed > 0 
    ? ((stats.handsWon / stats.handsPlayed) * 100).toFixed(1) 
    : 0;
  
  const netChips = stats.totalChipsWon - stats.totalChipsLost;
  const avgWin = stats.handsWon > 0 
    ? Math.round(stats.totalChipsWon / stats.handsWon) 
    : 0;
  
  const avgLoss = (stats.handsPlayed - stats.handsWon) > 0
    ? Math.round(stats.totalChipsLost / (stats.handsPlayed - stats.handsWon))
    : 0;
  
  // Most common hand
  let mostCommonHand = null;
  let maxCount = 0;
  Object.entries(stats.handTypeCounts || {}).forEach(([hand, count]) => {
    if (count > maxCount) {
      maxCount = count;
      mostCommonHand = hand;
    }
  });
  
  return {
    ...stats,
    winRate,
    netChips,
    avgWin,
    avgLoss,
    mostCommonHand,
    mostCommonHandCount: maxCount
  };
};

// Reset stats
export const resetStats = () => {
  const freshStats = { ...DEFAULT_STATS, sessionStart: new Date().toISOString() };
  saveStats(freshStats);
  return freshStats;
};

// Format stats for display
export const formatSessionDuration = (startDate) => {
  if (!startDate) return '0m';
  
  const start = new Date(startDate);
  const now = new Date();
  const diffMs = now - start;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  
  if (diffHours > 0) {
    return `${diffHours}h ${diffMins % 60}m`;
  }
  return `${diffMins}m`;
};

export default {
  loadStats,
  saveStats,
  updateStats,
  calculateDerivedStats,
  resetStats,
  formatSessionDuration
};
